import {
  ReactNode,
  useContext,
  ChangeEvent,
  useEffect,
  memo,
  useMemo,
} from 'react';
import {
  capitalize,
  Chip,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  JobType,
  PropertyGroup,
  Violation,
  PhoneType,
  PropertyListItem,
  Status,
  PropertyViolation,
} from 'types';
import { ACTION_TYPE, JobBuilderContext } from 'context/jobBuilderContext';
import { useTranslation } from 'react-i18next';
import ViolationsAutocomplete from 'components/autocompletes/ViolationsAutocomplete';
import AuthService from 'utils/AuthService';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import AuturaDateTimePicker from 'components/inputs/AuturaDateTimePicker';
import { useFetcher } from 'react-router';
import { DEFAULT_BRANDING } from 'context/themeContext';
import { SelectValidator } from 'react-material-ui-form-validator';
import useTenantID from 'hooks/useTenantID';
import { Warning } from '@mui/icons-material';
import { ROUTES, routeWithParams } from 'router/routes';
import { JobPropertyData } from './jobPropertyDataLoader';

export interface GeneralStepProps {
  fetcherKey: string;
}

const GeneralStep = ({ fetcherKey }: GeneralStepProps): ReactNode => {
  const { t } = useTranslation();
  const tenantID = useTenantID();
  const { state: jobState, dispatch } = useContext(JobBuilderContext);
  const fetcher = useFetcher({ key: fetcherKey });

  const hideGroup = !(AuthService.IsAdmin() || AuthService.IsSuperAdmin());

  const filteredProperties: PropertyListItem[] = useMemo(() => {
    // if no property group is selected, return all properties
    if (!jobState.propertyGroup) return jobState.propertyListOptions;

    // if the no group is selected, return all properties that are not in a group
    if (jobState.propertyGroup.externalID === 'nogroup') {
      const propertyGroupProperties: Array<{ id: number; tenantID: number }> =
        [];
      jobState.propertyGroupListOptions.forEach((pg) => {
        pg.properties.forEach((p) => {
          propertyGroupProperties.push({ id: p.id, tenantID: p.tenantID || 0 });
        });
      });
      return jobState.propertyListOptions.filter(
        (p) => !propertyGroupProperties.some((pgp) => pgp.id === p.id),
      );
    }

    // if a group is selected, return all properties in that group
    if (jobState.propertyGroup) {
      return jobState.propertyListOptions.filter((p) =>
        jobState?.propertyGroup?.properties.some((pgp) => pgp.id === p.id),
      );
    }
    return [];
  }, [
    jobState.propertyGroup,
    jobState.propertyListOptions,
    jobState.propertyGroupListOptions,
  ]);

  const NO_GROUP: PropertyGroup = {
    id: 0,
    name: t('property_group.noGroup'),
    contactID: 0,
    externalID: 'nogroup',
    description: '',
    status: Status.Active,
    properties: [],
    tenantID: 0,
    rules: [],
    rulesets: [],
    permitTypes: [],
    qrCodes: [],
    users: [],
    contacts: [],
    data: {
      settings: {},
      branding: DEFAULT_BRANDING,
    },
  };

  const propertyFetcher = useFetcher<JobPropertyData>({ key: `property-data-${fetcherKey}` });

  useEffect(() => {
    if (propertyFetcher.data && propertyFetcher.state === 'idle') {
      dispatch({
        type: ACTION_TYPE.SET_PROPERTY,
        payload: propertyFetcher.data.property,
      });
      dispatch({
        type: ACTION_TYPE.SET_LOCATION,
        payload: propertyFetcher.data.property.location,
      });
      dispatch({
        type: ACTION_TYPE.SET_VIOLATION_LIST_OPTIONS,
        payload: propertyFetcher.data.violations,
      });
    }
  }, [propertyFetcher.data, propertyFetcher.state, dispatch]);

  const handlePropertyGroupChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value as string;
    const pg =
      jobState.propertyGroupListOptions.find((pg) => pg.name === name) ||
      NO_GROUP;
    dispatch({
      type: ACTION_TYPE.SET_PROPERTY_GROUP,
      payload: pg,
    });
  };

  const handlePropertyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value as string;
    const option =
      jobState.propertyListOptions.find((p) => p.name === name) || null;
    dispatch({ type: ACTION_TYPE.SET_VIOLATIONS, payload: [] });
    if (option) {
      const url = routeWithParams(ROUTES.JOB_PROPERTY_DATA, [
        tenantID,
        option.id,
      ]);
      propertyFetcher.load(url);
    } else {
      dispatch({ type: ACTION_TYPE.SET_PROPERTY, payload: null });
      dispatch({ type: ACTION_TYPE.SET_VIOLATION_LIST_OPTIONS, payload: [] });
    }
  };

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_TIME, payload: new Date() });
  }, []);

  const handleJobTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: ACTION_TYPE.SET_JOB_TYPE,
      payload: event.target.value as JobType,
    });
  };

  const setDateTime = (dateTime: Date | null) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_REQUEST_TIME,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.SET_REQUEST_TIME,
      payload: dateTime,
    });
  };

  const setPhoneNumber = (phoneNumber: string) => {
    dispatch({
      type: ACTION_TYPE.SET_CONTACT_PHONE,
      payload: phoneNumber,
    });
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_PHONE,
      payload: true,
    });
  };

  const setPhoneType = (phoneType: PhoneType) => {
    dispatch({
      type: ACTION_TYPE.SET_CONTACT_PHONE_TYPE,
      payload: phoneType,
    });
  };

  return (
    <Grid container width={'100%'} spacing={1.5}>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <AuturaDropdown
          label={`${t('job.type', { ns: 'entities' })}`}
          name={'jobType'}
          value={jobState.jobType || JobType.TOW}
          setValue={handleJobTypeChange}
          required
          options={[
            // JobType.NONE,
            JobType.TOW,
            // JobType.PATROL,
            // JobType.BOOT,
            JobType.TAG,
          ]}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <Grid spacing={1.5} container>
          {!hideGroup && (
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <SelectValidator
                fullWidth
                margin='none'
                variant='outlined'
                name={`property-group-select`}
                label={`${t('labels.property_group_select', { ns: 'jobs' })}`}
                value={jobState.propertyGroup?.name || ''}
                onChange={handlePropertyGroupChange}
                disabled={
                  jobState.propertyGroupListOptions.length === 0 ||
                  fetcher.state === 'loading' ||
                  propertyFetcher.state === 'loading'
                }
              >
                <MenuItem key={'nogroup'} value={NO_GROUP.name} divider>
                  <Typography sx={{ fontWeight: 'bold' }} variant='body1'>
                    {t('labels.no_group', { ns: 'properties' })}
                  </Typography>
                </MenuItem>
                {jobState.propertyGroupListOptions.map((pg) => {
                  return (
                    <MenuItem key={pg.id} value={pg.name} divider>
                      <Typography variant='body1'>
                        {capitalize(pg.name)}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </SelectValidator>
            </Grid>
          )}
          <Grid
            size={{
              xs: 12,
              sm: hideGroup ? 12 : 6,
            }}
          >
            <SelectValidator
              fullWidth
              margin='none'
              variant='outlined'
              name={`property-select`}
              label={`${t('property.label', { ns: 'entities' })} *`}
              value={jobState.property?.name || ''}
              disabled={
                fetcher.state === 'loading' ||
                propertyFetcher.state === 'loading'
              }
              onChange={handlePropertyChange}
              validators={['required']}
              errorMessages={[
                t('errors.required', {
                  name: t('property.label', { ns: 'entities' }),
                }),
              ]}
            >
              {filteredProperties.map((p) => {
                return (
                  <MenuItem
                    key={p.id}
                    value={p.name}
                    divider
                    disabled={p.disableTowing}
                  >
                    <Stack
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      width={'100%'}
                      display={'flex'}
                    >
                      <Typography variant={'body1'} noWrap>
                        {p.name}
                      </Typography>
                      {p.disableTowing && (
                        <Chip
                          color={'error'}
                          icon={<Warning />}
                          label={t('donottow.label', { ns: 'entities' })}
                          variant={'outlined'}
                          size={'small'}
                          sx={{
                            fontWeight: 'bold',
                            px: 1,
                            ml: 2,
                          }}
                        />
                      )}
                    </Stack>
                  </MenuItem>
                );
              })}
            </SelectValidator>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <AuturaPhoneField
          name={'contactPhone'}
          label={t('job.contactPhone', { ns: 'entities' })}
          typeFieldName={'contactPhoneType'}
          typeFieldLabel='Type'
          phoneNumber={jobState.contactPhone}
          setPhoneNumber={setPhoneNumber}
          phoneType={jobState.contactPhoneType}
          setPhoneType={setPhoneType}
          required
          readonly={fetcher.state !== 'idle' || propertyFetcher.state !== 'idle'}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <AuturaDateTimePicker
          label={t('job.requestTime', { ns: 'entities' })}
          dateTime={jobState.requestTime}
          setDateTime={setDateTime}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <ViolationsAutocomplete
          disableAllViolations
          options={jobState.violationListOptions}
          selected={jobState.violations}
          loading={
            fetcher.state === 'loading' || propertyFetcher.state === 'loading'
          }
          handleChange={(violations: Violation[] | PropertyViolation[]) => {
            dispatch({
              type: ACTION_TYPE.SET_VIOLATIONS,
              payload: violations,
            });
          }}
          required
          disabled={
            jobState.violationListOptions.length === 0 ||
            fetcher.state === 'loading' ||
            propertyFetcher.state === 'loading'
          }
        />
      </Grid>
    </Grid>
  );
};

export default memo(GeneralStep);
